import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, MapPin, Briefcase } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/layout/Breadcrumbs';

const FALCO_PHOTO = "https://customer-assets.emergentagent.com/job_54396c88-8cf9-4315-926b-d20a7ab432d7/artifacts/uqz5vdch_2026-03-21_13-47-36.png";

export default function SeoConsultantPillar() {
  const { language } = useLanguage();
  const basePath = language === 'en' ? '/en' : '';

  const cities = [
    { slug: 'amsterdam', name: 'Amsterdam' },
    { slug: 'veenendaal', name: 'Veenendaal' },
    { slug: 'utrecht', name: 'Utrecht' },
    { slug: 'wageningen', name: 'Wageningen' },
    { slug: 'ede', name: 'Ede' },
    { slug: 'hengelo', name: 'Hengelo' },
    { slug: 'emmen', name: 'Emmen' },
    { slug: 'asten', name: 'Asten' },
  ];

  const sectors = [
    { slug: 'restaurant', name: 'Restaurant' },
    { slug: 'mkb', name: 'MKB' },
    { slug: 'loodgieter', name: language === 'nl' ? 'Loodgieter' : 'Plumber' },
    { slug: 'webshop', name: 'Webshop' },
    { slug: 'advocaat', name: language === 'nl' ? 'Advocaat' : 'Lawyer' },
    { slug: 'makelaar', name: language === 'nl' ? 'Makelaar' : 'Real Estate Agent' },
    { slug: 'zorg', name: language === 'nl' ? 'Zorg' : 'Healthcare' },
    { slug: 'horeca', name: 'Horeca' },
    { slug: 'fysiotherapeut', name: language === 'nl' ? 'Fysiotherapeut' : 'Physiotherapist' },
    { slug: 'gehandicaptenzorg', name: language === 'nl' ? 'Gehandicaptenzorg' : 'Disability Care' },
    { slug: 'orthodontist', name: 'Orthodontist' },
    { slug: 'dermatoloog', name: language === 'nl' ? 'Dermatoloog' : 'Dermatologist' },
    { slug: 'vastgoed', name: language === 'nl' ? 'Vastgoed' : 'Real Estate' },
    { slug: 'jurist', name: language === 'nl' ? 'Jurist' : 'Legal Professional' },
    { slug: 'implantologie', name: 'Implantologie' },
    { slug: 'accountant', name: 'Accountant' },
    { slug: 'huisarts', name: language === 'nl' ? 'Huisarts' : 'General Practitioner' },
    { slug: 'logopedie', name: 'Logopedie' },
  ];

  return (
    <div data-testid="seo-consultant-pillar">
      <SEOHead
        title={language === 'nl' ? 'Freelance SEO Specialist Inhuren | Kesult' : 'Hire a native Dutch SEO consultant | Freelance, strategic, results-driven'}
        description={language === 'nl'
          ? 'Op zoek naar een freelance SEO specialist? Falco Keiman helpt bedrijven in heel Nederland met data-gedreven SEO-strategieën. Bekijk alle steden en sectoren.'
          : 'Looking for a native Dutch SEO freelancer? Deep expertise in travel, ecommerce and financial services. Holistic SEO strategies tailored to the Dutch search landscape.'}
        canonical="https://www.kesult.com/seo-consultant"
        lang={language}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          "name": "Kesult",
          "description": language === 'nl' 
            ? "Freelance SEO specialist inhuren voor data-gedreven SEO-strategieën" 
            : "SEO consultant helping international businesses boost organic growth in the Dutch market",
          "url": "https://www.kesult.com/seo-consultant",
          "provider": {
            "@type": "Person",
            "name": "Falco Keiman",
            "jobTitle": "Freelance SEO Consultant"
          },
          "areaServed": {
            "@type": "Country",
            "name": "Netherlands"
          }
        }}
      />
      {/* Hero */}
      <section className="relative bg-[#0B1A2E] text-white overflow-hidden" data-testid="pillar-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-32 lg:pb-40 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="max-w-xl">
              <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-5">
                Freelance SEO Consultant
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-bold leading-tight mb-6" data-testid="pillar-title">
                {language === 'nl' ? 'Freelance SEO specialist inhuren' : 'Hire a native Dutch SEO consultant'}
              </h1>
              <p className="text-lg text-white/70 leading-relaxed mb-8">
                {language === 'nl'
                  ? 'Ik help bedrijven in heel Nederland met data-gedreven SEO-strategieën die daadwerkelijk resultaat opleveren. Geen standaard playbook, maar een aanpak op maat — afgestemd op jouw business, jouw markt en jouw doelen.'
                  : 'A freelance SEO specialist who is a native Dutch speaker, understands the Dutch search landscape and has proven track records in travel, ecommerce and financial services. I work as your strategic partner — not as an agency with layers of overhead.'}
              </p>
              <Link to={`${basePath}/contact`}>
                <button className="bg-[#4286F4] text-white font-semibold px-8 py-3.5 rounded-full hover:bg-[#3070d4] transition-colors inline-flex items-center gap-2" data-testid="pillar-cta">
                  {language === 'nl' ? 'Neem contact op' : 'Get in touch'}
                  <ArrowRight className="h-4 w-4" />
                </button>
              </Link>
            </div>
            <div className="hidden lg:flex justify-end">
              <img
                src={FALCO_PHOTO}
                alt="Falco Keiman - Freelance SEO Consultant"
                className="w-full max-w-[380px] aspect-[3/4] object-cover object-top rounded-3xl opacity-90"
              />
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-white" style={{ clipPath: 'polygon(0 100%, 100% 0, 100% 100%)' }} />
      </section>

      <Breadcrumbs />

      {/* Intro */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-3">
              {language === 'nl' ? 'Over Kesult' : 'Why hire me'}
            </p>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#08080A] mb-6">
              {language === 'nl' ? 'Samenwerken met een SEO specialist' : 'A strategic SEO partner who knows the Dutch market inside out'}
            </h2>
            <div className="space-y-4 text-[#555] leading-relaxed">
              <p>
                {language === 'nl'
                  ? 'Steeds meer bedrijven zoeken hun producten en diensten online. Daar wil je als ondernemer op inspelen, maar niet iedereen heeft de tijd of kennis om dit zelf op te pakken. Een freelance SEO specialist kan dat werk uit handen nemen.'
                  : 'I bring deep expertise in verticals where organic search is a primary growth channel — particularly travel, ecommerce and financial services. My experience at companies like Trivago taught me how to operate in highly competitive, multi-market environments where every ranking position counts.'}
              </p>
              <p>
                {language === 'nl'
                  ? 'Ik werk hoofdzakelijk strategisch: ik zet de richting uit en werk samen met in-house marketeers, developers of externe partners die het uitvoerende werk doen. Zo wordt iedereen in zijn kracht gebruikt en behaal je het beste resultaat.'
                  : 'As a native Dutch speaker, I understand the nuances of Dutch search behaviour, local link building opportunities and what it takes to rank on Google.nl. I work strategically — setting the direction and collaborating with your in-house team or local partners for execution.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Cities - Only shown for Dutch */}
      {language === 'nl' && (
      <section className="py-20 lg:py-28 bg-[#FAFAFA]" data-testid="pillar-cities">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-3">
            <MapPin className="inline h-4 w-4 mr-1 -mt-0.5" />
            Steden
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#08080A] mb-10">
            SEO Consultant per stad
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {cities.map((city) => (
              <Link
                key={city.slug}
                to={`${basePath}/seo-consultant/${city.slug}`}
                className="group p-5 rounded-2xl border border-gray-100 bg-white hover:shadow-lg hover:border-[#4286F4]/20 transition-all duration-300"
                data-testid={`pillar-city-${city.slug}`}
              >
                <h3 className="font-bold text-[#08080A] group-hover:text-[#4286F4] transition-colors mb-1">
                  SEO {city.name}
                </h3>
                <span className="inline-flex items-center gap-1 text-[#4286F4] text-sm font-semibold group-hover:gap-2 transition-all">
                  Bekijk
                  <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Sectors - Only shown for Dutch */}
      {language === 'nl' && (
      <section className="py-20 lg:py-28 bg-white" data-testid="pillar-sectors">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-3">
            <Briefcase className="inline h-4 w-4 mr-1 -mt-0.5" />
            Sectoren
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#08080A] mb-10">
            SEO Consultant per sector
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {sectors.map((sector) => (
              <Link
                key={sector.slug}
                to={`/seo-consultant/${sector.slug}`}
                className="group p-5 rounded-2xl border border-gray-100 bg-white hover:shadow-lg hover:border-[#4286F4]/20 transition-all duration-300"
                data-testid={`pillar-sector-${sector.slug}`}
              >
                <h3 className="font-bold text-[#08080A] group-hover:text-[#4286F4] transition-colors mb-1">
                  {sector.name}
                </h3>
                <span className="inline-flex items-center gap-1 text-[#4286F4] text-sm font-semibold group-hover:gap-2 transition-all">
                  Bekijk
                  <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* CTA */}
      <section className="py-16 bg-[#4286F4] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <h2 className="text-2xl sm:text-3xl font-bold">
            {language === 'nl' ? 'Klaar om te groeien?' : 'Ready to boost your organic growth in NL?'}
          </h2>
          <div className="flex gap-4">
            <Link to={`${basePath}/contact`}>
              <button className="bg-white text-[#4286F4] font-semibold px-8 py-3.5 rounded-full hover:bg-gray-50 transition-colors inline-flex items-center gap-2">
                {language === 'nl' ? 'Neem contact op' : 'Get in touch'}
                <ArrowRight className="h-4 w-4" />
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
